import {
	AlertOutlined,
	ArrowLeftOutlined,
	ClockCircleOutlined,
	MessageOutlined,
	UsergroupAddOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { Button, InputNumber, Select, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canSendManualSms } from "@/shared/config/permissions";
import { formatMoney } from "@/shared/utils/money";
import {
	type BackendDebtClient,
	type DebtReportParams,
	getDebtReport,
} from "../services/debts.api";

type DebtRow = {
	id: string;
	clientId: string;
	clientName: string;
	buildingName: string;
	roomNumber: string;
	totalDebt: number;
	overdueDebt: number;
	delayedRows: number;
	maxDelayDays: number;
	lastPaymentDate: string;
	managerName: string;
};

function formatDate(dateStr: string | null): string {
	if (!dateStr || dateStr === "-") {
		return "-";
	}
	try {
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) {
			return dateStr;
		}
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	} catch {
		return dateStr;
	}
}

function mapBackendClient(c: BackendDebtClient): DebtRow {
	return {
		id: c.clientId,
		clientId: c.clientId,
		clientName: c.fullName,
		buildingName: c.buildingName,
		roomNumber: c.roomNumber,
		totalDebt: c.totalDebt,
		overdueDebt: c.overdueAmount,
		delayedRows: c.overdueCount,
		maxDelayDays: c.maxDelayDays,
		lastPaymentDate: formatDate(c.lastPaymentDate),
		managerName: c.managerName,
	};
}

export function DebtsReportPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const [building, setBuilding] = useState("all");
	const [manager, setManager] = useState("all");
	const [minDelay, setMinDelay] = useState<number | null>(
		(location.state as any)?.minDelay || null
	);
	const [displayClients, setDisplayClients] = useState<DebtRow[]>([]);
	const [allClients, setAllClients] = useState<DebtRow[]>([]);
	const [loading, setLoading] = useState(true);

	const fromDashboard = (location.state as any)?.fromDashboard === true;

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const params: DebtReportParams = {};
				if (building !== "all") {
					params.buildingId = building;
				}
				if (manager !== "all") {
					params.managerId = manager;
				}
				if (minDelay !== null) {
					params.minDaysOverdue = minDelay;
				}

				const hasFilters = Object.keys(params).length > 0;

				const [filteredResult, allResult] = await Promise.all([
					getDebtReport(hasFilters ? params : undefined),
					hasFilters ? getDebtReport() : Promise.resolve(undefined),
				]);

				if (cancelled) {
					return;
				}

				const displayRows = filteredResult.clients.map(mapBackendClient);
				setDisplayClients(displayRows);

				if (allResult) {
					setAllClients(allResult.clients.map(mapBackendClient));
				} else {
					setAllClients(displayRows);
				}
			} catch {
				if (!cancelled) {
					setDisplayClients([]);
					setAllClients([]);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}

		load();
		return () => {
			cancelled = true;
		};
	}, [building, manager, minDelay]);

	const buildingOptions = useMemo(
		() => Array.from(new Set(allClients.map((row) => row.buildingName))),
		[allClients]
	);
	const managerOptions = useMemo(
		() => Array.from(new Set(allClients.map((row) => row.managerName))),
		[allClients]
	);

	const totalDebt = displayClients.reduce((sum, row) => sum + row.totalDebt, 0);
	const overdueDebt = displayClients.reduce((sum, row) => sum + row.overdueDebt, 0);
	const delayedRowsCount = displayClients.reduce((sum, row) => sum + row.delayedRows, 0);
	const maxDelay = Math.max(...displayClients.map((row) => row.maxDelayDays), 0);
	const canSendSms = canSendManualSms(role);

	if (loading) {
		return (
			<div
				className="crm-debts-report"
				style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}
			>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="crm-debts-report">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<ArrowLeftOutlined />}
								onClick={() => navigate(-1)}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>Qarzdorlik hisoboti</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Qarzdor mijozlar va kechikkan to'lovlar
					</p>
				</div>
				<div className="crm-report-actions">
					<Select
						options={[
							{ label: "Barcha bino", value: "all" },
							...buildingOptions.map((item) => ({ label: item, value: item })),
						]}
						value={building}
						onChange={setBuilding}
					/>
					<Select
						options={[
							{ label: "Barcha menejer", value: "all" },
							...managerOptions.map((item) => ({ label: item, value: item })),
						]}
						value={manager}
						onChange={setManager}
					/>
					<InputNumber
						min={0}
						placeholder="Min. kechikish kun"
						value={minDelay}
						onChange={(value) => setMinDelay(value)}
					/>
				</div>
			</div>

			<section className="crm-report-kpis">
				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<UsergroupAddOutlined />
						</span>
						Qarzdorlar
					</div>
					<div className="crm-kpi-value">
						<strong>{displayClients.length}</strong>
						<span>ta</span>
					</div>
					<div className="crm-kpi-note">filtr bo'yicha mijozlar</div>
				</div>

				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon sky">
							<WalletOutlined />
						</span>
						Jami qarz
					</div>
					<div className="crm-kpi-value">
						<strong>{formatMoney(totalDebt)}</strong>
					</div>
					<div className="crm-kpi-note">qolgan shartnoma summasi</div>
				</div>

				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<AlertOutlined />
						</span>
						Kechikkan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatMoney(overdueDebt)}</strong>
					</div>
					<div className="crm-kpi-note">{delayedRowsCount} ta grafik qatori</div>
				</div>

				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon red">
							<ClockCircleOutlined />
						</span>
						Eng uzun kechikish
					</div>
					<div className="crm-kpi-value">
						<strong>{maxDelay}</strong>
						<span>kun</span>
					</div>
					<div className="crm-kpi-note">maksimal delayDays</div>
				</div>
			</section>

			<div className="crm-card crm-table-card crm-debts-table-card">
				<div className="crm-card-head">
					<h3>Qarzdor mijozlar</h3>
					<span>Jami {displayClients.length} qarzdor</span>
				</div>
				<div className="crm-table-wrap">
					<table className="crm-table">
						<thead>
							<tr>
								<th>Mijoz</th>
								<th>Bino / Xona</th>
								<th style={{ textAlign: "right" }}>Jami qarz</th>
								<th style={{ textAlign: "right" }}>Kechikkan</th>
								<th style={{ textAlign: "center" }}>Qator</th>
								<th>Oxirgi to'lov</th>
								<th>Menejer</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{displayClients.map((row, index) => (
								<tr
									key={`${row.id}-${index}`}
									onClick={() =>
										navigate(`/clients/${row.clientId}`, { state: { returnTo: "/reports/debts" } })
									}
								>
									<td>
										<strong>{row.clientName}</strong>
										<span className="crm-table-sub">kechikish: {row.maxDelayDays} kun</span>
									</td>
									<td>
										{row.buildingName} / {row.roomNumber}
									</td>
									<td style={{ textAlign: "right" }}>
										<span className="num">{formatMoney(row.totalDebt)}</span>
									</td>
									<td style={{ textAlign: "right" }}>
										<span className={row.overdueDebt > 0 ? "crm-debt-danger num" : "num"}>
											{formatMoney(row.overdueDebt)}
										</span>
									</td>
									<td style={{ textAlign: "center" }}>
										<span className="crm-badge amber">{row.delayedRows}</span>
									</td>
									<td style={{ whiteSpace: "nowrap" }}>{row.lastPaymentDate}</td>
									<td>{row.managerName}</td>
									<td style={{ textAlign: "right" }}>
										{canSendSms ? (
											<Button
												type="text"
												shape="circle"
												icon={
													<MessageOutlined
														style={{ fontSize: "16px", color: "var(--crm-ink-3)" }}
													/>
												}
												onClick={(event) => {
													event.stopPropagation();
													navigate(`/sms/send?clientId=${row.clientId}`);
												}}
											/>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="crm-table-foot">
					<span>Qarzdorlik yoshi va undirish holati shu jadvalda kuzatiladi</span>
				</div>
			</div>
		</div>
	);
}

export default DebtsReportPage;
